import { useEffect, useState } from 'react';
import { useAuth } from './lib/auth';
import { ThemeToggle } from './components/ThemeToggle';
import { Login } from './pages/Login';
import { Enroll } from './pages/Enroll';
import { Dashboard } from './pages/Dashboard';
import { Demos } from './pages/Demos';
import { Integrations } from './pages/Integrations';
import { DemoPage } from './pages/DemoPage';
import { ShellPage } from './pages/ShellPage';

const stripSlash = (p: string) => p.replace(/\/+$/, '') || '/';
// Lightweight client-side route: the dedicated demo walkthrough opens in its own tab at /demo?…
// (index.html fallback is served for this path by both the daemon and CloudFront). Still auth-gated.
const isDemoRoute = stripSlash(window.location.pathname) === '/demo';
// The console session (interactive shell) likewise opens in its own tab at /shell?system=… — auth-gated.
const isShellRoute = stripSlash(window.location.pathname) === '/shell';

export function App() {
  const { user, loading, refresh } = useAuth();
  const [enrolling, setEnrolling] = useState(false);

  if (loading) {
    return (
      <main className="center">
        <p className="muted">Loading…</p>
      </main>
    );
  }

  if (user) return isDemoRoute ? <DemoPage /> : isShellRoute ? <ShellPage /> : <AuthedApp />;

  if (enrolling) {
    return (
      <Enroll
        onDone={async () => {
          setEnrolling(false);
          await refresh();
        }}
        onCancel={() => setEnrolling(false)}
      />
    );
  }

  return <Login onEnrollStart={() => setEnrolling(true)} onAuthenticated={refresh} />;
}

type View = 'dashboard' | 'demos' | 'integrations';
const viewOf = (path: string): View =>
  stripSlash(path) === '/integrations' ? 'integrations' : stripSlash(path) === '/demos' ? 'demos' : 'dashboard';

/** The signed-in app shell: shared top bar + nav, switching between the Dashboard and Integrations. */
function AuthedApp() {
  const { user, logout } = useAuth();
  const [view, setView] = useState<View>(viewOf(window.location.pathname));

  useEffect(() => {
    const onPop = () => setView(viewOf(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = (v: View) => {
    const path = v === 'integrations' ? '/integrations' : v === 'demos' ? '/demos' : '/';
    if (stripSlash(window.location.pathname) !== stripSlash(path)) window.history.pushState({}, '', path);
    setView(v);
  };

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">
          <span className="brand-mark">AIStor</span> Demo Hub
        </span>
        <nav className="topnav">
          <button
            type="button"
            className={`navlink ${view === 'dashboard' ? 'active' : ''}`}
            onClick={() => navigate('dashboard')}
          >
            Dashboard
          </button>
          <button
            type="button"
            className={`navlink ${view === 'demos' ? 'active' : ''}`}
            onClick={() => navigate('demos')}
          >
            Demos
          </button>
          <button
            type="button"
            className={`navlink ${view === 'integrations' ? 'active' : ''}`}
            onClick={() => navigate('integrations')}
          >
            Integrations
          </button>
        </nav>
        <span className="spacer" />
        <ThemeToggle />
        <span className="muted">{user?.email}</span>
        <button type="button" className="link" onClick={() => void logout()}>
          Sign out
        </button>
      </header>

      {view === 'integrations' ? <Integrations /> : view === 'demos' ? <Demos /> : <Dashboard />}
    </div>
  );
}
